import { useCallback, useMemo, useRef, useState } from "react";

import { DocumentGroup } from "@/api";

import { PendingDocumentMove, isAssetInventoryZone } from "../DropZoneDnd.types";
import { PendingMoveZoneHandlers } from "../PendingDocumentMovesContext";

function assetZoneOf(move: PendingDocumentMove): DocumentGroup {
  return isAssetInventoryZone(move.source) ? move.source : move.target;
}

/**
 * Hosts the pending-move state shared between the always-mounted dropzone panel and the asset
 * inventory views. The mounted view owns the moves in its form values and registers handlers
 * here (ref-based, so registering never re-renders the layout); the `pendingMoves` state is a
 * read-only mirror the panel sections render from.
 */
export function usePendingMovesStore() {
  const [pendingMoves, setPendingMoves] = useState<PendingDocumentMove[]>([]);
  const handlersRef = useRef(new Map<DocumentGroup, PendingMoveZoneHandlers>());

  const registerAssetZone = useCallback((zone: DocumentGroup, handlers: PendingMoveZoneHandlers) => {
    handlersRef.current.set(zone, handlers);
    return () => {
      handlersRef.current.delete(zone);
    };
  }, []);

  const publishPendingMoves = useCallback((moves: PendingDocumentMove[]) => {
    setPendingMoves(moves);
  }, []);

  const removePendingMove = useCallback((move: PendingDocumentMove) => {
    handlersRef.current.get(assetZoneOf(move))?.removeMove(move.document.documentId);
  }, []);

  /** Routes a drop touching an asset zone to the mounted view. Returns false if none is mounted. */
  const tryAddPendingMove = useCallback((move: PendingDocumentMove): boolean => {
    const handlers = handlersRef.current.get(assetZoneOf(move));
    if (!handlers) {
      return false;
    }
    handlers.addMove(move);
    return true;
  }, []);

  const contextValue = useMemo(
    () => ({ pendingMoves, removePendingMove, registerAssetZone, publishPendingMoves }),
    [pendingMoves, removePendingMove, registerAssetZone, publishPendingMoves]
  );

  return { contextValue, tryAddPendingMove };
}
